/**
 * Seeded deterministic PRNG for the sim. Lets us generate STATIC, reproducible
 * randomness "at conception" — e.g. a lock's pin/tumbler layout is rolled once
 * from a seed derived from its tile and never re-rolled, satisfying CLAUDE.md
 * rule 1 (deterministic sim). Headless — no PixiJS, no render deps (rule 4).
 *
 * `mulberry32` mirrors the render-layer noise PRNG; promoted here so sim code
 * doesn't reach into `render/`.
 */

/** mulberry32 — a small, fast, well-distributed 32-bit PRNG. Returns a function
 *  yielding floats in [0, 1). Same seed → same stream. */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Fold several integers into one 32-bit seed (order-sensitive). Use to derive a
 *  per-entity seed from a base seed + tile coords, e.g. `hashSeed(mapSeed,x,y)`. */
export function hashSeed(...nums: number[]): number {
  let h = 2166136261 >>> 0; // FNV-1a offset basis
  for (const n of nums) {
    h ^= n | 0;
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h >>> 0;
}
